import {PermissionKey} from "@bounce/shared/enums/permissions";
import React, {createContext, PropsWithChildren, useContext, useEffect, useMemo, useState} from "react";
import {useGetSpringUser} from "apis/requests/backoffice/roles-management/auth";
import {useGetResolutionExpertByEmail} from "apis/requests/backoffice/roles-management/getReByEmail";
import {Role, RolePermissions} from "declerations/server/role_models";
import {useAuth} from "@bounce/shared/contexts";

export interface BackofficeUserContextProps {
  rolePermission?: RolePermissions;
  hasSpringPermission: boolean;
  resolutionExpertRoles: Role[] | undefined;
}

const BackofficeUserContext = createContext<BackofficeUserContextProps>(undefined!);

export const BackofficeUserProvider = ({children}: PropsWithChildren) => {
  const {credentials, token, agentEmail} = useAuth();
  const {data: resolutionExpert} = useGetResolutionExpertByEmail({agentEmail}, {enabled: !!agentEmail});

  const resolutionExpertRoles = resolutionExpert?.roles;
  const [rolePermission, setRolePermission] = useState<RolePermissions>(null);

  const enableSpringUser = !!credentials?.agentEmail && !!token;

  const {data: currentUser} = useGetSpringUser({
    enabled: enableSpringUser,
  });

  useEffect(() => {
    if (currentUser?.permissions) {
      setRolePermission(currentUser.permissions);
    }
  }, [currentUser]);

  const hasSpringPermission = useMemo(() => {
    if (!rolePermission) return false;
    return rolePermission.some((permission) => permission === PermissionKey.ALL || permission.startsWith("spring."));
  }, [rolePermission]);

  return (
    <BackofficeUserContext.Provider
      value={{
        rolePermission,
        hasSpringPermission,
        resolutionExpertRoles,
      }}
    >
      {children}
    </BackofficeUserContext.Provider>
  );
};

export const useBackofficeUser = (): BackofficeUserContextProps => {
  const context = useContext(BackofficeUserContext);
  if (!context) throw new Error("BackofficeUserContext must be used inside BackofficeUserProvider");
  return context;
};
